import {
  InformationCircleIcon,
  ChartBarIcon,
  SunIcon,
  UserGroupIcon,
} from '@heroicons/react/outline'
import { useState, useEffect } from 'react'
import GraphemeSplitter from 'grapheme-splitter'
import { Alert } from './components/alerts/Alert'
import { Grid } from './components/grid/Grid'
import { Keyboard } from './components/keyboard/Keyboard'
import { AboutModal } from './components/modals/AboutModal'
import { InfoModal } from './components/modals/InfoModal'
import { StatsModal } from './components/modals/StatsModal'
import { RegisterModal } from './components/modals/RegisterModal'
import { LeaderboardModal } from './components/modals/LeaderboardModal'
import { submitResult, loginUser } from './lib/api'
import {
  GAME_TITLE,
  WIN_MESSAGES,
  GAME_COPIED_MESSAGE,
  ABOUT_GAME_MESSAGE,
  NOT_ENOUGH_LETTERS_MESSAGE,
  WORD_NOT_FOUND_MESSAGE,
  INVALID_HAND_MESSAGE,
  CORRECT_WORD_MESSAGE,
  KEYBOARD_SHORTCUT_REMINDER_MESSAGE,
} from './constants/strings'
import {
  isWordInWordList,
  isInvalidHand,
  isWinningWord,
  solution,
  isTsumo,
  wind,
  loadGameForServerCount,
} from './lib/words'
import { addStatsForCompletedGame, loadStats } from './lib/stats'
import { HAND_SIZE, GUESS_MAX } from './constants/settings'

import './App.css'

const ALERT_TIME_MS = 2000
const graphemeSplitter = new GraphemeSplitter()

const windMap: { [id: number]: string } = {
  1: '東',
  2: '南',
  3: '西',
  4: '北',
}

function App() {
  const prefersDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches
  const forceYakumanPreview =
    new URLSearchParams(window.location.search).get('yakuman') === '1'

  const [currentGuess, setCurrentGuess] = useState(() => localStorage.getItem('presetHand') || '')
  const [isGameWon, setIsGameWon] = useState(false)
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false)
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false)
  const [isNotEnoughLetters, setIsNotEnoughLetters] = useState(false)
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false)
  const [isWordNotFoundAlertOpen, setIsWordNotFoundAlertOpen] = useState(false)
  const [isInvalidHandAlertOpen, setIsInvalidHandAlertOpen] = useState(false)
  const [isSubmittingResult, setIsSubmittingResult] = useState(false)
  const [isGameLost, setIsGameLost] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme')
      ? localStorage.getItem('theme') === 'dark'
      : prefersDarkMode
      ? true
      : false
  )
  const [successAlert, setSuccessAlert] = useState('')
  const [currentWind, setCurrentWind] = useState(wind)
  const [currentIsTsumo, setCurrentIsTsumo] = useState(isTsumo)
  const [isYakumanRound, setIsYakumanRound] = useState(forceYakumanPreview)
  const [guesses, setGuesses] = useState<string[]>([])
  const [username, setUsername] = useState(() => localStorage.getItem('username') || '')
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(() => !localStorage.getItem('username') || !localStorage.getItem('pin'))
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false)
  const [presetHand, setPresetHand] = useState(() => localStorage.getItem('presetHand') || '')

  const [stats, setStats] = useState(() => loadStats())
  const [serverGamesPlayed, setServerGamesPlayed] = useState(0)
  const [hasLoadedServerGames, setHasLoadedServerGames] = useState(false)
  const [persistentRedeemCode, setPersistentRedeemCode] = useState<string | null>(null)
  const [redeemAlert, setRedeemAlert] = useState(
    () => new URLSearchParams(window.location.search).get('redeem') || ''
  )

  const canShowProgressCounters = !!username && hasLoadedServerGames
  const gamesUntilReward = Math.max(0, 100 - serverGamesPlayed)
  const gamesUntilYakuman = 20 - (serverGamesPlayed % 20)
  const hasCompletedGame = isGameWon || isGameLost

  useEffect(() => {
    const pin = localStorage.getItem('pin') || ''
    if (username && pin) {
      loginUser(username, pin).then((r) => {
        if (r.ok && r.user) {
          setServerGamesPlayed(r.user.gamesPlayed)
          setPersistentRedeemCode(r.user.redeemCode || null)
          const result = loadGameForServerCount(r.user.gamesPlayed)
          setCurrentWind(result.wind)
          setCurrentIsTsumo(result.isTsumo)
          setIsYakumanRound(forceYakumanPreview || result.isYakuman)
          setGuesses([])
          setCurrentGuess(presetHand)
          setIsGameWon(false)
          setIsGameLost(false)
        }
      }).catch(() => {}).finally(() => setHasLoadedServerGames(true))
    } else {
      setHasLoadedServerGames(!username)
    }
  }, [forceYakumanPreview, presetHand, username])

  const handleSubmitResultResponse = (r: Awaited<ReturnType<typeof submitResult>>) => {
    if (r?.user) {
      setServerGamesPlayed(r.user.gamesPlayed)
      setPersistentRedeemCode(r.user.redeemCode || null)
      setHasLoadedServerGames(true)
    }
    if (r?.redeemCode) setRedeemAlert(r.redeemCode)
  }

  const handleNextGame = () => {
    const result = loadGameForServerCount(serverGamesPlayed)
    setCurrentWind(result.wind)
    setCurrentIsTsumo(result.isTsumo)
    setIsYakumanRound(forceYakumanPreview || result.isYakuman)
    setGuesses([])
    setCurrentGuess(presetHand)
    setIsGameWon(false)
    setIsGameLost(false)
    setSuccessAlert('')
  }

  const handleSavePreset = () => {
    const trimmed = currentGuess.replace(/[1-9]/g, '')
    if (graphemeSplitter.splitGraphemes(trimmed).length === HAND_SIZE) {
      setPresetHand(trimmed)
      localStorage.setItem('presetHand', trimmed)
      setSuccessAlert('已保存为默认首手')
      setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
    } else {
      setSuccessAlert('请先输入完整的14张牌')
      setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
    }
  }

  const handleClearPreset = () => {
    setPresetHand('')
    localStorage.removeItem('presetHand')
    setSuccessAlert('已清除默认首手')
    setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
  }

  useEffect(() => {
    const classList = document.documentElement.classList

    if (isDarkMode) {
      classList.remove('light')
      classList.add('dark')
    } else {
      classList.remove('dark')
      classList.add('light')
    }
  }, [isDarkMode])

  const handleDarkMode = (isDark: boolean) => {
    setIsDarkMode(isDark)
    localStorage.setItem('theme', isDark ? 'dark' : 'light')
  }

  useEffect(() => {
    if (isGameWon) {
      setSuccessAlert(
        WIN_MESSAGES[Math.floor(Math.random() * WIN_MESSAGES.length)]
      )
      setTimeout(() => {
        setSuccessAlert('')
      }, ALERT_TIME_MS)
    }
    if (isGameLost) {
      setTimeout(() => {}, ALERT_TIME_MS)
    }
  }, [isGameWon, isGameLost])

  const onChar = (value: string) => {
    if (value === 'm' || value === 'p' || value === 's' || value === 'z') {
      let lookupTable = {
        m: ['🀇', '🀈', '🀉', '🀊', '🀋', '🀌', '🀍', '🀎', '🀏'],
        p: ['🀙', '🀚', '🀛', '🀜', '🀝', '🀞', '🀟', '🀠', '🀡'],
        s: ['🀐', '🀑', '🀒', '🀓', '🀔', '🀕', '🀖', '🀗', '🀘'],
        z: ['🀀', '🀁', '🀂', '🀃', '🀆', '🀅', '🀄', '', ''],
      }
      setCurrentGuess(
        currentGuess.replace(
          /[1-9]/g,
          (match) => lookupTable[value][parseInt(match) - 1]
        )
      )
    } else if (guesses.length < GUESS_MAX && !isGameWon) {
      let trimmedGuess = currentGuess.replaceAll(/[1-9]/g, '')

      if (
        '1' <= value &&
        value <= '9' &&
        graphemeSplitter.splitGraphemes(currentGuess).length < HAND_SIZE
      ) {
        setCurrentGuess(`${currentGuess}${value}`)
      } else if (
        value > '9' &&
        graphemeSplitter.splitGraphemes(trimmedGuess).length < HAND_SIZE
      ) {
        setCurrentGuess(`${trimmedGuess}${value}`)
      }
    }
  }

  const onDelete = () => {
    setCurrentGuess(
      graphemeSplitter.splitGraphemes(currentGuess).slice(0, -1).join('')
    )
  }

  const onEnter = () => {
    if (isGameWon || isGameLost) {
      return
    }
    if (
      !(
        graphemeSplitter.splitGraphemes(currentGuess.replace(/[1-9]/g, ''))
          .length === HAND_SIZE
      )
    ) {
      setIsNotEnoughLetters(true)
      return setTimeout(() => {
        setIsNotEnoughLetters(false)
      }, ALERT_TIME_MS)
    }

    if (!isInvalidHand(currentGuess)) {
      setIsInvalidHandAlertOpen(true)
      return setTimeout(() => {
        setIsInvalidHandAlertOpen(false)
      }, ALERT_TIME_MS)
    }

    if (!isWordInWordList(currentGuess)) {
      setIsWordNotFoundAlertOpen(true)
      return setTimeout(() => {
        setIsWordNotFoundAlertOpen(false)
      }, ALERT_TIME_MS)
    }

    const winningWord = isWinningWord(currentGuess)

    if (
      graphemeSplitter.splitGraphemes(currentGuess).length === HAND_SIZE &&
      guesses.length < GUESS_MAX &&
      !isGameWon
    ) {
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      if (winningWord) {
        setStats(addStatsForCompletedGame(stats, guesses.length))
        if (username) {
          setIsSubmittingResult(true)
          submitResult(username, guesses.length + 1, true).then(
            handleSubmitResultResponse
          ).finally(() => setIsSubmittingResult(false))
        }
        setIsGameWon(true)
        return setIsStatsModalOpen(true)
      }

      if (guesses.length === GUESS_MAX - 1) {
        setStats(addStatsForCompletedGame(stats, guesses.length + 1))
        if (username) {
          setIsSubmittingResult(true)
          submitResult(username, GUESS_MAX, false).then(
            handleSubmitResultResponse
          ).finally(() => setIsSubmittingResult(false))
        }
        setIsGameLost(true)
        setIsStatsModalOpen(true)
      }
    }
  }

  return (
    <div className="py-8 max-w-7xl mx-auto sm:px-6 lg:px-8">
      <div className="flex w-80 mx-auto items-center mb-8 mt-12">
        <h1 className="text-xl grow font-bold dark:text-white">{GAME_TITLE}</h1>
        <SunIcon
          className="h-6 w-6 cursor-pointer dark:stroke-white"
          onClick={() => handleDarkMode(!isDarkMode)}
        />
        <InformationCircleIcon
          className="h-6 w-6 cursor-pointer dark:stroke-white"
          onClick={() => setIsInfoModalOpen(true)}
        />
        <ChartBarIcon
          className="h-6 w-6 cursor-pointer dark:stroke-white"
          onClick={() => setIsStatsModalOpen(true)}
        />
        <UserGroupIcon
          className="h-6 w-6 cursor-pointer dark:stroke-white"
          onClick={() => setIsLeaderboardOpen(true)}
        />
      </div>
      <div className="w-80 mx-auto text-center text-xs mt-2 space-y-1">
        {canShowProgressCounters ? (
          <>
            {persistentRedeemCode ? (
              <p className="dark:text-gray-300">
                兑换码：<span className="font-bold text-purple-500 mx-1">{persistentRedeemCode}</span>
              </p>
            ) : (
              <p className="dark:text-gray-300">
                还有<span className="font-bold text-amber-500 mx-1">{gamesUntilReward}</span>场可以获得<span className="font-bold text-purple-500 mx-1">神秘礼品</span>
              </p>
            )}
            <p className="dark:text-gray-300">
              还有<span className="font-bold text-cyan-500 mx-1">{gamesUntilYakuman}</span>场出现罕见的<span className="font-bold text-red-500 mx-1">役满</span>！
            </p>
          </>
        ) : (
          <p className="dark:text-gray-300">正在同步场次...</p>
        )}
      </div>
      <div className="flex w-full mx-auto items-center mb-8 mt-12">
        <div className="w-full text-center">
          {isYakumanRound && (
            <div className="mb-2 text-sm font-bold text-amber-600 dark:text-amber-300">
              役满局
            </div>
          )}
          <h2 className="text-lg font-bold dark:text-white">
            场风：{windMap[Math.floor(currentWind / 10)]} / 自风：{' '}
            {windMap[currentWind % 10]} / {currentIsTsumo ? '自摸' : '荣和'}
          </h2>
        </div>
      </div>
      <Grid
        guesses={guesses}
        currentGuess={currentGuess}
        isYakumanRound={isYakumanRound}
      />
      <Keyboard
        onChar={onChar}
        onDelete={onDelete}
        onEnter={onEnter}
        guesses={guesses}
      />
      {hasCompletedGame && (
        <button
          type="button"
          className="mx-auto mt-4 flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 select-none disabled:opacity-50"
          disabled={isSubmittingResult}
          onClick={handleNextGame}
        >
          {isSubmittingResult ? '同步中...' : '下一局'}
        </button>
      )}
      <InfoModal
        isOpen={isInfoModalOpen}
        handleClose={() => setIsInfoModalOpen(false)}
      />
      <StatsModal
        isOpen={isStatsModalOpen}
        handleClose={() => setIsStatsModalOpen(false)}
        guesses={guesses}
        gameStats={stats}
        isGameLost={isGameLost}
        isGameWon={isGameWon}
        handleShare={() => {
          setSuccessAlert(GAME_COPIED_MESSAGE)
          return setTimeout(() => setSuccessAlert(''), ALERT_TIME_MS)
        }}
        handleNextGame={handleNextGame}
        isNextGameDisabled={isSubmittingResult}
        username={username}
      />
      <AboutModal
        isOpen={isAboutModalOpen}
        handleClose={() => setIsAboutModalOpen(false)}
      />
      <RegisterModal
        isOpen={isRegisterModalOpen}
        defaultUsername={username}
        onRegistered={(name, userData) => {
          setUsername(name)
          localStorage.setItem('username', name)
          if (userData) {
            setServerGamesPlayed(userData.gamesPlayed)
            setPersistentRedeemCode(userData.redeemCode || null)
          }
          setIsRegisterModalOpen(false)
        }}
      />
      <LeaderboardModal
        isOpen={isLeaderboardOpen}
        handleClose={() => setIsLeaderboardOpen(false)}
        currentUsername={username}
      />

      <button
        type="button"
        className="mx-auto mt-8 flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 select-none"
        onClick={() => setIsAboutModalOpen(true)}
      >
        {ABOUT_GAME_MESSAGE}
      </button>

      <div className="mx-auto mt-4 flex items-center gap-2 justify-center">
        <button
          type="button"
          className="flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 select-none"
          onClick={handleSavePreset}
        >
          保存当前为默认首手
        </button>
        {presetHand && (
          <button
            type="button"
            className="flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 select-none"
            onClick={handleClearPreset}
          >
            清除
          </button>
        )}
      </div>

      <Alert
        message={`${
          NOT_ENOUGH_LETTERS_MESSAGE +
          (currentGuess.match(/[1-9]/g)
            ? KEYBOARD_SHORTCUT_REMINDER_MESSAGE
            : '')
        }`}
        isOpen={isNotEnoughLetters}
      />
      <Alert
        message={WORD_NOT_FOUND_MESSAGE}
        isOpen={isWordNotFoundAlertOpen}
      />
      <Alert message={INVALID_HAND_MESSAGE} isOpen={isInvalidHandAlertOpen} />
      <Alert message={CORRECT_WORD_MESSAGE(solution)} isOpen={isGameLost} />
      <Alert
        message={successAlert}
        isOpen={successAlert !== ''}
        variant="success"
      />
      <Alert
        message={`恭喜！你的兑换码: ${redeemAlert}`}
        isOpen={redeemAlert !== ''}
        variant="success"
      />
    </div>
  )
}

export default App
